import { motion, useScroll, useTransform } from "framer-motion";
import { Link } from "react-router-dom";
import {
  ArrowRight,
  HandHeart,
  Users,
  Gift,
  Store,
  Heart,
} from "lucide-react";
import { KineticHeading } from "../components/KineticHeading";
import { Reveal } from "../components/Reveal";
import { Marquee } from "../components/Marquee";
import { StageTracker } from "../components/StageTracker";
import { useSettings } from "../context/SettingsContext";
import {
  IMAGES,
  FEED_PHOTOS,
  IG_REEL_URL,
  IG_REEL_EMBED,
  HOLIDAY_FAIR,
} from "../lib/content";
import { money, nextSunday } from "../lib/api";

export default function Home() {
  const { settings } = useSettings();
  const L = settings?.ledger;
  const stage = settings?.stage ?? 2;
  const censusUrl = settings?.census_url;
  const { scrollY } = useScroll();
  const heroImgY = useTransform(scrollY, [0, 900], [0, 140]);

  return (
    <>
      {/* HERO */}
      <section
        className="band relative flex min-h-[92vh] items-end overflow-hidden"
        data-testid="home-hero"
      >
        <motion.div style={{ y: heroImgY }} className="absolute inset-0 -bottom-44">
          <img
            src={IMAGES.hero}
            alt="The Nā Pali coastline of Kauaʻi"
            className="h-full w-full scale-105 object-cover"
            data-testid="hero-image"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-[#0E2A20] via-[#0E2A20]/70 to-[#0E2A20]/20" />
        </motion.div>
        <div className="wrap relative grid items-end gap-10 pb-16 pt-40 sm:pb-20 lg:grid-cols-[1.35fr_1fr]">
          <div>
            <motion.p
              className="eyebrow !text-band-foreground/80"
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
            >
              Hurricane Lowell emergency relief · Kauaʻi
            </motion.p>
            <KineticHeading
              lines={[
                { text: "Kauaʻi takes care" },
                {
                  text: "of Kauaʻi.",
                  className: "font-serifg italic font-semibold",
                },
              ]}
              className="mt-4 font-display text-[2.8rem] font-extrabold leading-[1.04] tracking-tight text-band-foreground sm:text-6xl lg:text-7xl"
              delay={0.15}
              testid="hero-headline"
            />
            <motion.p
              className="mt-6 max-w-xl text-lg leading-relaxed text-band-foreground/85 sm:text-xl"
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.6, duration: 0.7 }}
            >
              <b>Priced Out of Kauaʻi by Lowell</b> supports the local
              businesses that help make our island home, and the families who
              depend on them.
            </motion.p>
            <motion.div
              className="mt-8 flex flex-wrap gap-3"
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.75, duration: 0.7 }}
            >
              <Link to="/resources" className="btn" data-testid="hero-resources-cta">
                See Immediate Resources
              </Link>
              <a
                className="btn btn-ghost"
                href={censusUrl}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="hero-census-cta"
              >
                Complete the Business Census
              </a>
            </motion.div>
          </div>
          <motion.aside
            className="glass-card"
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.9, duration: 0.7 }}
            data-testid="immediate-needs-card"
          >
            <p className="eyebrow !text-band-foreground/70">
              Immediate needs come first
            </p>
            <p className="leading-relaxed text-band-foreground/90">
              Looking for food, water, shelter, power or medical help? Start
              with our verified resource list. In a life-threatening emergency,
              call 911.
            </p>
            <Link
              to="/resources"
              className="inline-flex items-center gap-2 font-bold text-band-foreground"
              data-testid="hero-resources-link"
            >
              See Immediate Resources <ArrowRight className="h-4 w-4" />
            </Link>
          </motion.aside>
        </div>
      </section>

      <Marquee />

      {/* TRACKER */}
      <section className="wrap py-20 sm:py-24">
        <Reveal className="flex max-w-2xl flex-col gap-4">
          <p className="eyebrow">Where the program stands</p>
          <h2 className="font-display text-3xl font-bold tracking-tight sm:text-4xl">
            Three stages, in order
          </h2>
          <p className="leading-relaxed text-muted-foreground">
            We share resources now, learn the scope of business needs next,
            and only open applications once the Board has approved a written,
            fair process.
          </p>
        </Reveal>
        <Reveal delay={0.1} className="mt-10">
          <StageTracker stage={stage} />
        </Reveal>
      </section>

      {/* FEED THE COMMUNITY */}
      <section className="wrap py-20 sm:py-24" data-testid="feed-community-section">
        <Reveal className="flex max-w-3xl flex-col items-start gap-4">
          <p className="eyebrow">Feed the Community</p>
          <h2 className="font-display text-3xl font-bold tracking-tight sm:text-4xl">
            Kauaʻi takes care of Kauaʻi, one plate at a time
          </h2>
          <p className="leading-relaxed text-muted-foreground">
            Our volunteers cook together, pack plates, and share free meals
            with neighbors along Kauaʻi's shores. Watch the reel — then come
            lend a hand.
          </p>
          <div className="mt-2 flex flex-wrap gap-3">
            <Link to="/get-involved" className="btn btn-sm" data-testid="feed-join-cta">
              Join as a Community Member
            </Link>
            <a
              className="btn btn-ghost btn-sm"
              href={IG_REEL_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="feed-ig-link"
            >
              <InstagramIcon /> Watch on Instagram
            </a>
          </div>
        </Reveal>
        <div className="mt-10 grid gap-4 md:grid-cols-2 lg:h-[560px] lg:grid-cols-4 lg:grid-rows-2">
          <Reveal className="h-72 md:h-full lg:row-span-2">
            <div className="group h-full w-full overflow-hidden rounded-2xl">
              <img
                src={FEED_PHOTOS[2].src}
                alt={FEED_PHOTOS[2].alt}
                loading="lazy"
                className="h-full w-full object-cover transition duration-700 group-hover:scale-105"
                data-testid="feed-photo-3"
              />
            </div>
          </Reveal>
          <Reveal delay={0.05} className="h-72 lg:h-full">
            <div className="group h-full w-full overflow-hidden rounded-2xl">
              <img
                src={FEED_PHOTOS[0].src}
                alt={FEED_PHOTOS[0].alt}
                loading="lazy"
                className="h-full w-full object-cover transition duration-700 group-hover:scale-105"
                data-testid="feed-photo-1"
              />
            </div>
          </Reveal>
          <Reveal
            delay={0.1}
            className="h-[520px] md:h-full lg:row-span-2"
          >
            <div
              className="h-full w-full overflow-hidden rounded-2xl border border-border bg-card"
              data-testid="instagram-reel-embed"
            >
              <iframe
                src={IG_REEL_EMBED}
                title="Feed the Community — Instagram reel"
                className="h-full w-full"
                frameBorder="0"
                scrolling="no"
                loading="lazy"
                allowFullScreen
              />
            </div>
          </Reveal>
          <Reveal delay={0.15} className="h-72 lg:h-full">
            <div className="group h-full w-full overflow-hidden rounded-2xl">
              <img
                src={FEED_PHOTOS[1].src}
                alt={FEED_PHOTOS[1].alt}
                loading="lazy"
                className="h-full w-full object-cover transition duration-700 group-hover:scale-105"
                data-testid="feed-photo-2"
              />
            </div>
          </Reveal>
          <Reveal delay={0.2} className="h-72 md:col-span-2 lg:col-span-1 lg:h-full">
            <div className="group h-full w-full overflow-hidden rounded-2xl">
              <img
                src={FEED_PHOTOS[3].src}
                alt={FEED_PHOTOS[3].alt}
                loading="lazy"
                className="h-full w-full object-cover transition duration-700 group-hover:scale-105"
                data-testid="feed-photo-4"
              />
            </div>
          </Reveal>
        </div>
      </section>

      {/* COMMUNITY MEMBERSHIP */}
      <section
        className="border-t border-border bg-muted/40"
        data-testid="membership-section"
      >
        <div className="wrap grid gap-10 py-20 sm:py-24 lg:grid-cols-2">
          <Reveal className="flex flex-col items-start gap-4">
            <p className="eyebrow">Community Membership</p>
            <h2 className="font-display text-3xl font-bold tracking-tight sm:text-4xl">
              Membership that lifts local businesses, ʻohana, and community
              needs
            </h2>
            <p className="leading-relaxed text-muted-foreground">
              Community Membership is free, and it is how neighbors power this
              work — showing up for local shops, lending hands at Feed the
              Community meal days, helping plan the Holiday Fair, and keeping
              Kauaʻi connected when times get hard. One sign-up, and you'll
              know about every volunteer call, event, and update first.
            </p>
            <Link to="/get-involved" className="btn" data-testid="membership-registration-cta">
              Open the membership form
            </Link>
          </Reveal>
          <div className="grid gap-4 sm:grid-cols-3 lg:grid-cols-1 xl:grid-cols-3">
            <Reveal delay={0.05} className="h-full">
              <div className="card-surface h-full !gap-2 !p-5">
                <Store className="h-6 w-6 text-terra" aria-hidden="true" />
                <h3 className="font-display text-base font-bold leading-snug">
                  For local businesses
                </h3>
                <p className="text-sm leading-relaxed text-muted-foreground">
                  Members volunteer, spread the word, and show up for the local
                  shops and vendors that make Kauaʻi home.
                </p>
              </div>
            </Reveal>
            <Reveal delay={0.1} className="h-full">
              <div className="card-surface h-full !gap-2 !p-5">
                <Heart className="h-6 w-6 text-terra" aria-hidden="true" />
                <h3 className="font-display text-base font-bold leading-snug">
                  For ʻohana
                </h3>
                <p className="text-sm leading-relaxed text-muted-foreground">
                  Members bring neighbors and families together — from beachside
                  meal days to the Holiday Fair.
                </p>
              </div>
            </Reveal>
            <Reveal delay={0.15} className="h-full">
              <div className="card-surface h-full !gap-2 !p-5">
                <HandHeart className="h-6 w-6 text-terra" aria-hidden="true" />
                <h3 className="font-display text-base font-bold leading-snug">
                  For community needs
                </h3>
                <p className="text-sm leading-relaxed text-muted-foreground">
                  Members help spot needs early and share verified resources
                  with the people who need them most.
                </p>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* WAYS TO HELP */}
      <section className="wrap py-20 sm:py-24">
        <Reveal className="flex flex-col gap-4">
          <p className="eyebrow">Ways to help</p>
          <h2 className="font-display text-3xl font-bold tracking-tight sm:text-4xl">
            Help keep Hawaiʻi local
          </h2>
        </Reveal>
        <div className="mt-10 grid gap-5 md:grid-cols-3">
          <Reveal>
            <div className="card-surface h-full">
              <HandHeart className="h-8 w-8 text-terra" aria-hidden="true" />
              <h3 className="font-display text-xl font-bold">Give</h3>
              <p className="leading-relaxed text-muted-foreground">
                Donations support every Kamalama program — immediate community
                response, Feed the Community meal days, the Holiday Fair, and
                Hurricane Lowell recovery.
              </p>
              <Link
                to="/donate"
                className="btn btn-sm mt-auto w-fit"
                data-testid="home-donate-link"
              >
                Donate
              </Link>
            </div>
          </Reveal>
          <Reveal delay={0.08}>
            <div className="card-surface h-full">
              <Users className="h-8 w-8 text-terra" aria-hidden="true" />
              <h3 className="font-display text-xl font-bold">
                Join as a Community Member
              </h3>
              <p className="leading-relaxed text-muted-foreground">
                Free sign-up for updates, events, donated resources, and future
                volunteer opportunities.
              </p>
              <Link
                to="/get-involved"
                className="btn btn-ghost btn-sm mt-auto w-fit"
                data-testid="home-join-link"
              >
                Sign up
              </Link>
            </div>
          </Reveal>
          <Reveal delay={0.16}>
            <div className="card-surface h-full">
              <Gift className="h-8 w-8 text-terra" aria-hidden="true" />
              <h3 className="font-display text-xl font-bold">
                Kamalama Holiday Fair
              </h3>
              <p className="leading-relaxed text-muted-foreground">
                Local vendors, ʻohana food, keiki fun, and holiday cheer —{" "}
                <b className="text-foreground">
                  Saturday, {HOLIDAY_FAIR.dateShort}
                </b>
                , {HOLIDAY_FAIR.hours}.
              </p>
              <Link
                to="/holiday-fair"
                className="btn btn-ghost btn-sm mt-auto w-fit"
                data-testid="home-holiday-fair-link"
              >
                Event & vendor info
              </Link>
            </div>
          </Reveal>
        </div>
      </section>

      {/* SUNDAY PREVIEW */}
      <section className="border-t border-border">
        <div className="wrap grid gap-10 py-20 sm:py-24 lg:grid-cols-2">
          <Reveal className="flex flex-col items-start gap-4">
            <p className="eyebrow">Transparency & impact</p>
            <h2 className="font-display text-3xl font-bold tracking-tight sm:text-4xl">
              Updated every Sunday
            </h2>
            <p className="leading-relaxed text-muted-foreground">
              Every Sunday we post verified figures: funds raised, processing
              and fundraising costs, funds available for the program, and
              census progress. After awards begin, we add funds distributed
              and businesses assisted.
            </p>
            <Link
              to="/transparency"
              className="link-underline inline-flex items-center gap-2 font-semibold text-primary dark:text-accent"
              data-testid="home-transparency-link"
            >
              View the Sunday update <ArrowRight className="h-4 w-4" />
            </Link>
          </Reveal>
          <Reveal delay={0.12}>
            <div className="card-surface" data-testid="home-ledger-preview">
              <p className="font-mono text-xs text-muted-foreground">
                {L ? `Update of Sunday, ${L.date}` : `First update: ${nextSunday()}`}
              </p>
              <table className="ledger-table">
                <tbody>
                  <tr>
                    <th>Total funds raised</th>
                    <td>{L ? money(L.raised) : "—"}</td>
                  </tr>
                  <tr>
                    <th>Processing & fundraising expenses</th>
                    <td>{L ? money(L.expenses) : "—"}</td>
                  </tr>
                  <tr className="total">
                    <th>Net funds available for the program</th>
                    <td>{L ? money(L.raised - L.expenses) : "—"}</td>
                  </tr>
                </tbody>
              </table>
              <Link
                to="/transparency"
                className="link-underline w-fit text-sm font-semibold text-primary dark:text-accent"
                data-testid="home-ledger-full-link"
              >
                Full update
              </Link>
            </div>
          </Reveal>
        </div>
      </section>

      {/* QUOTE BAND */}
      <section className="band relative overflow-hidden" data-testid="quote-band">
        <img
          src={IMAGES.coastSunset}
          alt=""
          aria-hidden="true"
          className="absolute inset-0 h-full w-full object-cover opacity-25"
        />
        <div className="absolute inset-0 bg-band/60" aria-hidden="true" />
        <div className="wrap relative flex flex-col items-start gap-6 py-24">
          <p className="eyebrow !text-band-foreground/70">Our promise</p>
          <p className="max-w-3xl font-serifg text-3xl italic leading-snug text-band-foreground sm:text-5xl">
            “Kauaʻi takes care of Kauaʻi — every Sunday, in plain numbers, for
            everyone to see.”
          </p>
        </div>
      </section>
    </>
  );
}

const InstagramIcon = () => (
  <svg
    className="h-4 w-4"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <rect width="20" height="20" x="2" y="2" rx="5" ry="5" />
    <path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z" />
    <line x1="17.5" x2="17.51" y1="6.5" y2="6.5" />
  </svg>
);
